import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { formatDuration, formatTimeStamp, getCallDurationInSeconds } from "../../api/callData";
import { useCallData } from "../../contexts/CallDataContext";
import { useWeek } from "../../contexts/WeekContext";
import { useCustomer } from "../../contexts/CustomerContext";
import { addDays, isWithinInterval, startOfDay, endOfDay } from "date-fns";
import { parseCustomTimestamp } from "../../utils/dateUtils";
import { getOrderStatus, getEquipmentList } from "../../utils/bookingUtils";

import { Icon } from "@iconify/react/dist/iconify.js";

// Client-specific column configuration
const CLIENT_COLUMN_CONFIG = {
    'wards': {
        showEquipment: true,
        showCallId: false
    },
    'celeste': {
        showEquipment: false,
        showCallId: true
    },
    'helios': {
        showEquipment: false,
        showCallId: true
    },
    'default': {
        showEquipment: true,
        showCallId: false
    }
};

const getBookingStatusStyles = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'confirmed') {
        return { textColor: 'tag-green text-green-500', icon: 'material-symbols:check-circle-outline' };
    }
    if (statusLower === 'incomplete') {
        return { textColor: 'tag-orange text-orange-500', icon: 'carbon:incomplete-error' };
    }
    if (statusLower?.includes('completed')) {
        return { textColor: 'tag-green text-green-500', icon: 'material-symbols:check-circle-outline' };
    }
    return { textColor: 'tag-gray text-font-secondary', icon: 'ph:question' };
};

const getTrendStyles = (trend) => {
    const styles = {
        positive: {
            textColor: "tag-green text-green-500",
            icon: "iconamoon:trend-up-light"
        },
        negative: {
            textColor: "tag-red text-red-500",
            icon: "iconamoon:trend-down-light"
        },
        neutral: {
            textColor: "tag-gray text-font-secondary",
            icon: "mdi:trending-neutral"
        },
    };

    return styles[trend] || styles.neutral;
};

const CallTable = ({ onRowClick, isShort, callRow, displayData, selectedRows, setSelectedRows }) => {
    const { dateRange } = useWeek();
    const { getEffectiveCustomerId } = useCustomer();

    const [copiedId, setCopiedId] = useState(null);
    const [sortedData, setSortedData] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10); // Fixed at 10 items per page
    const tableBodyRef = useRef(null); // Ref for the table body container
    
    // Store the page number where the selected call is located
    const [selectedCallPage, setSelectedCallPage] = useState(null);

    // Using fixed pagination, no calculation state needed

    const { data } = useCallData();

    // Get client-specific column configuration
    const clientId = getEffectiveCustomerId();
    const columnConfig = CLIENT_COLUMN_CONFIG[clientId] || CLIENT_COLUMN_CONFIG.default;
    const showEquipment = columnConfig.showEquipment;
    const showCallId = columnConfig.showCallId;

    useEffect(() => {
        setSortedData(displayData || []);
    }, [displayData]);

    // No need for layout effect with fixed pagination

    // Handle isShort changes (CallDetails opens/closes)
    useEffect(() => {
        // Force data refresh when isShort changes
        if (displayData) {
            setSortedData([...displayData]);
        }
        
        // When CallDetails is closed (isShort becomes false), restore the page where the call was selected
        if (!isShort && selectedCallPage !== null) {
            setCurrentPage(selectedCallPage);
            setSelectedCallPage(null); // Reset after use
        }
    }, [isShort, displayData, selectedCallPage]);

    const selectAll = (e) => {
        if(e.target.checked) {
            // Get unique IDs only
            const uniqueIds = [...new Set(
                data
                    .map((call) => call?.metadata?.stream_sid)
                    .filter(sid => sid !== null && sid !== undefined)
            )];
            setSelectedRows(uniqueIds);
        } else {
            setSelectedRows([]);
        }
    };

    const selectRow = (id) => {
        if (!id) return; // Guard against null/undefined ids
        setSelectedRows((prev) => {
            return prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        });
    };

    // --- DATA SLICING FOR PAGINATION ---
    // Filter out null/undefined stream_sid values first to avoid issues with duplicate IDs
    const validData = sortedData.filter(call => call?.metadata?.stream_sid !== null && call?.metadata?.stream_sid !== undefined);
    
    // Handle potential duplicate IDs by creating a map with the most recent entry for each ID
    const uniqueDataMap = new Map();
    validData.forEach(call => {
        uniqueDataMap.set(call.metadata.stream_sid, call);
    });
    
    // Convert map back to array and calculate pagination
    const uniqueData = Array.from(uniqueDataMap.values());
    const totalPages = Math.ceil(uniqueData.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedData = uniqueData.slice(startIndex, startIndex + rowsPerPage);


    return (
        <div
            className={
                `w-full h-full flex flex-col`
            }
        >
            {/* Desktop Table Header (xl and above) */}
            <div className="flex-shrink-0 hidden md:block">
                <table className="w-full table-auto text-left border-collapse text-sm">
                    <thead className=" ">
                        <tr className={`
                            text-font-primary h-[48px] font-semibold card-container rounded-md`}
                        >
                            <th className="pl-4 w-[16px]">
                                <input
                                    type="checkbox"
                                    onChange={selectAll}
                                    checked={selectedRows.length === data.length && data.length > 0}
                                    className="w-4 h-4 flex-shrink-0 cursor-pointer"
                                />
                            </th>
                            {!isShort && showEquipment && (
                                <th className="w-[100px] truncate max-w-[100px] pr-2">Equipment</th>
                            )}
                            {!isShort && showCallId && (
                                <th className="w-[100px] truncate max-w-[100px] pr-2">Call Id</th>
                            )}
                            {!isShort && (
                                <th className="w-[50px] truncate max-w-[50px] pr-2">Phone Number</th>
                            )}
                            {!isShort && (
                                <th className="w-[50px] truncate max-w-[50px] pr-2">Duration</th>
                            )}
                            {!isShort && (
                                <th className="w-[70px] truncate max-w-[70px] pr-2">Start Time</th>
                            )}
                            <th className="w-[70px] truncate max-w-[70px] px-2">Sentiment</th>
                            <th className="w-[70px] max-w-[70px] px-2 whitespace-nowrap truncate">Success Evaluation</th>
                            <th className="w-[70px] max-w-[70px] px-2 whitespace-nowrap truncate">Booking Status</th>
                        </tr>
                    </thead>
                </table>
            </div>

            {/* Mobile Header (below xl) */}
            <div className="flex-shrink-0 md:hidden">
                <div className={`
                    flex items-center justify-between px-3 py-2 text-font-primary text-sm h-[48px] font-semibold card-container
                    ${!isShort ? '' : ''}`}
                >
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            onChange={selectAll}
                            checked={selectedRows.length === data.length && data.length > 0}
                            className="w-4 h-4 flex-shrink-0 cursor-pointer"
                        />
                        <span className="font-semibold text-font-primary">Select All</span>
                    </div>
                    <span className="font-normal text-xs text-font-secondary">
                        {selectedRows.length} selected
                    </span>
                </div>
            </div>
            <div ref={tableBodyRef} className="flex-1 overflow-y-auto scrollable min-h-0">
                {/* Desktop Table View (xl and above) */}
                <table className="w-full table-auto text-left border-collapse text-sm hidden md:table">
                    <tbody className="">
                        {paginatedData.map((call, index) => {
                            const bookingData = call.cart_data || call.booking_info;
                            const equipmentText = bookingData ? getEquipmentList(bookingData) : 'N/A';
                            // No need for row height measurement
                            return (
                                <tr key={`${currentPage}-${index}-${call?.metadata?.stream_sid || index}`}
                                    className={
                                        `h-[56px] text-font-secondary border-b border-border interactive-card-container cursor-pointer transition-colors ease-out-in duration-300
                                        ${callRow === call?.metadata?.stream_sid ? 'bg-primary-20' : ''}`
                                    }
                                    onClick={() => {
                                        // Store current page before opening call details
                                        setSelectedCallPage(currentPage);
                                        onRowClick && onRowClick(call);
                                    }}
                                >
                                    <td className="pl-4 w-[16px]">
                                        <input
                                        type="checkbox"
                                        className={`w-4 h-4 flex-shrink-0 cursor-pointer ${callRow === call?.metadata?.stream_sid ? '' : ''}`}
                                        checked={selectedRows.includes(call?.metadata?.stream_sid)}
                                        onClick={(e) => e.stopPropagation()} // Prevent row click
                                        onChange={() => selectRow(call?.metadata?.stream_sid)}
                                        />
                                    </td>
                                    {!isShort && showEquipment && (
                                        <td
                                            className="w-[100px] max-w-[100px] pr-2 text-font-primary font-semibold"
                                            title={equipmentText !== 'N/A' && equipmentText !== 'No Equipment' ? equipmentText : undefined}
                                        >
                                            <span className="block truncate">{equipmentText}</span>
                                        </td>
                                    )}
                                    {!isShort && showCallId && (
                                        <td className="w-[100px] truncate max-w-[100px] pr-2 text-font-primary font-semibold">{call?.metadata?.stream_sid || 'N/A'}</td>
                                    )}
                                    {!isShort && (
                                        <td className="w-[50px] truncate max-w-[50px] pr-2 font-medium">{call?.metadata?.caller_id || 'N/A'}</td>
                                    )}
                                    {!isShort && (
                                        <td className="w-[50px] truncate max-w-[50px] pr-2 font-medium">{formatDuration(getCallDurationInSeconds(call))}</td>
                                    )}
                                    {!isShort && (
                                        <td className="w-[70px] truncate max-w-[70px] pr-2 font-medium">{formatTimeStamp(call?.metadata?.timestamp)}</td>
                                    )}
                                    <td className="w-[70px] truncate max-w-[70px] p-2 capitalize">
                                        <div className={`w-fit h-fit p-1.5 text-xs flex items-center gap-1 justify-center rounded-md font-semibold
                                            ${callRow === call.metadata.stream_sid ? 'h-[36px]' : ''}
                                            ${getTrendStyles(call.analytics.sentiment_analysis.trend).textColor}`}>
                                            <Icon icon={getTrendStyles(call.analytics.sentiment_analysis.trend).icon} className="text-base" />
                                            <span className="text-font-primary">{call.analytics.sentiment_analysis.trend}</span>
                                        </div>
                                    </td>
                                    <td className="w-[70px] truncate max-w-[70px] p-2">
                                        <div className={`
                                            w-fit h-fit p-1.5 text-xs flex items-center gap-1 justify-center rounded-md font-semibold
                                            ${call.success_score >= 7 ? 'tag-green text-green-500' : call.success_score >= 4 ? 'tag-orange text-orange-500' : 'tag-red text-red-500'}
                                            ${callRow === call.metadata.stream_sid ? 'h-[36px]' : ''}
                                        `}>
                                            <Icon icon={call.success_score >= 7 ? 'fa-regular:smile' : call.success_score >= 4 ? 'fontisto:neutral' : 'bx:sad'} className="text-base" />
                                            <span className="text-font-primary">{call.score_status || 'N/A'}</span>
                                        </div>
                                    </td>
                                    <td className="w-[70px] truncate max-w-[70px] p-2">
                                        {(call.booking_exists || call.cart_data) ?
                                            (() => {
                                                const bookingData = call.cart_data || call.booking_info;
                                                const status = getOrderStatus(bookingData);
                                                const { textColor, icon } = getBookingStatusStyles(status);
                                                return (
                                                    <div className={`w-fit h-fit p-1.5 text-xs flex items-center gap-1 justify-center rounded-md font-semibold ${callRow === call.metadata.stream_sid ? 'h-[36px]' : ''} ${textColor}`}>
                                                        <Icon icon={icon} className="text-lg" />
                                                        <span className="text-font-primary">{status}</span>
                                                    </div>
                                                );
                                            })()
                                            :
                                            <div className={`w-fit h-fit p-1.5 text-xs flex items-center gap-1 justify-center rounded-md font-semibold tag-gray ${callRow === call.metadata.stream_sid ? 'h-[36px]' : ''} text-font-secondary`}>
                                                <Icon icon="ph:empty" className="text-lg" />
                                                <span className="text-font-secondary">No Booking</span>
                                            </div>
                                        }
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* Mobile Card View (below xl) */}
                <div className="md:hidden space-y-2 py-2">
                    {paginatedData.map((call, index) => {
                        // No need for card height measurement
                        return (
                            <div
                                key={`${currentPage}-${index}-${call?.metadata?.stream_sid || index}`}
                                className={`
                                    p-3 rounded-lg flex flex-col gap-4 cursor-pointer transition-all duration-300 ease-out-in
                                    hover:shadow-lg hover:scale-[1.02]
                                    ${callRow === call?.metadata?.stream_sid ? '' : 'interactive-card-container bg-background'}
                                    ${callRow === call?.metadata?.stream_sid ? 'border-2 border-primary' : 'border border-border'}
                                `}
                                onClick={() => {
                                    // Store current page before opening call details
                                    setSelectedCallPage(currentPage);
                                    onRowClick && onRowClick(call);
                                }}
                            >
                                <div className="flex items-start justify-between ">
                                    <div className="flex flex-row w-full justify-between items-center gap-2">
                                        <div className="flex flex-row items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={selectedRows.includes(call?.metadata?.stream_sid)}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => selectRow(call?.metadata?.stream_sid)}
                                                className="w-4 h-4 flex-shrink-0 cursor-pointer"
                                            />
                                            <span className="font-semibold text-font-primary text-sm">
                                                {call?.metadata?.caller_id || 'N/A'}
                                            </span>
                                        </div>
                                        <span className="text-sm text-font-secondary">
                                            {formatTimeStamp(call?.metadata?.timestamp)}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className={`w-fit h-fit p-1.5 text-xs rounded flex items-center gap-1
                                            ${getTrendStyles(call.analytics.sentiment_analysis.trend).textColor}
                                            ${callRow === call.metadata.stream_sid ? '' : ''}`}>
                                            <Icon icon={getTrendStyles(call.analytics.sentiment_analysis.trend).icon} className="text-sm" />
                                            <span className="capitalize text-font-primary">{call.analytics.sentiment_analysis.trend}</span>
                                        </div>
                                        <div className={`w-fit h-fit p-1.5 text-xs rounded flex items-center gap-1
                                            ${call.success_score >= 7 ? 'tag-green text-green-500' : call.success_score >= 4 ? 'tag-orange text-orange-500' : 'tag-red text-red-500'}
                                            ${callRow === call.metadata.stream_sid ? '' : ''}`}>
                                            <Icon icon={call.success_score >= 7 ? 'fa-regular:smile' : call.success_score >= 4 ? 'fontisto:neutral' : 'bx:sad'} className="text-sm" />
                                            <span className="text-font-primary">{call.score_status || 'N/A'}</span>
                                        </div>
                                        {(call.booking_exists || call.cart_data) ?
                                            (() => {
                                                const bookingData = call.cart_data || call.booking_info;
                                                const status = getOrderStatus(bookingData);
                                                const { textColor, icon } = getBookingStatusStyles(status);
                                                return (
                                                    <div className={`w-fit h-fit p-1.5 text-xs rounded flex items-center gap-1 ${textColor} ${callRow === call.metadata.stream_sid ? '' : ''}`}>
                                                        <Icon icon={icon} className="text-sm" />
                                                        <span className="capitalize text-font-primary">{status}</span>
                                                    </div>
                                                );
                                            })()
                                            :
                                            <div className={`w-fit h-fit p-1.5 text-xs rounded flex items-center gap-1 text-font-secondary tag-gray ${callRow === call.metadata.stream_sid ? '' : ''}`}>
                                                <Icon icon="ph:question" className="text-sm" />
                                                <span className="text-font-secondary">No Booking</span>
                                            </div>
                                        }
                                    </div>
                                    <span className="text-sm text-font-secondary">
                                        {formatDuration(getCallDurationInSeconds(call))}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {paginatedData.length === 0 && (
                    <div className="text-center py-10 text-gray-400">No calls found.</div>
                )}
            </div>

            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-t border-bgAccent text-sm">
                <div className="hidden sm:block">
                    <span className="text-font-secondary">
                        Showing <span className="font-semibold text-primary">{uniqueData.length > 0 ? Math.min(startIndex + 1, uniqueData.length) : 0}</span> to <span className="font-semibold text-primary">{Math.min(startIndex + rowsPerPage, uniqueData.length)}</span> of <span className="font-semibold text-primary">{uniqueData.length}</span> results
                    </span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 px-1 sm:px-4 w-full sm:w-auto justify-between sm:justify-end">
                     <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                    >
                        <Icon icon="icon-park-outline:double-left" className="text-lg sm:text-xl text-accent" />
                    </button>
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                    >
                        <Icon icon="material-symbols:arrow-back-ios-new" className="text-base sm:text-lg text-accent" />
                    </button>
                    <span className="text-font-secondary text-xs sm:text-sm">
                        <span className="font-bold text-accent">{currentPage}</span>/<span className="font-bold">{totalPages}</span>
                    </span>
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                    >
                        <Icon icon="material-symbols:arrow-forward-ios" className="text-base sm:text-lg text-accent" />
                    </button>
                    <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
                    >
                        <Icon icon="icon-park-outline:double-right" className="text-lg sm:text-xl text-accent" />
                    </button>
                </div>
            </div>
        </div>
    )
};

export default CallTable;
